import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import API_URL from '../api';
import { Modal } from '../ui';
import PricingPlans from '../billing/PricingPlans';
import CheckoutModal from '../billing/CheckoutModal';
import { fetchPackages } from '../billing/checkout';
import '../billing/billing.css';

const ROLE_CATEGORIES = ['Engineering', 'Design', 'Marketing', 'Product', 'Operations', 'Finance', 'Sales', 'Content', 'Research', 'Data', 'Other'];
const POSITION_TYPES = ['Internship', 'Full-time', 'Part-time', 'Contract'];
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP'];
const SALARY_TYPES = ['Monthly', 'Annual'];
const DURATION_OPTIONS = ['1', '2', '3', '4', '5', '6', '8', '10', '12'];
const COLLEGES = [
  'IIT Bombay', 'IIT Delhi', 'IIT Madras', 'IIT Kanpur', 'IIT Kharagpur',
  'IIT Roorkee', 'IIT Guwahati', 'IIT Hyderabad', 'IIT Indore', 'IIT BHU',
  'IIT Jodhpur', 'IIT Patna', 'IIT Ropar', 'IIT Bhubaneswar', 'IIT Gandhinagar',
  'IIT Mandi', 'IIT Palakkad', 'IIT Tirupati', 'IIT Dhanbad', 'IIT Jammu',
  'IIT Bhilai', 'IIT Goa', 'IIT Dharwad',
  'NIT Trichy', 'NIT Surathkal', 'NIT Warangal', 'NIT Calicut', 'NIT Rourkela',
  'NIT Allahabad', 'NIT Kurukshetra', 'NIT Jaipur', 'NIT Surat',
  'BITS Pilani', 'BITS Goa', 'BITS Hyderabad',
  'IIIT Hyderabad', 'IIIT Bangalore', 'IIIT Delhi', 'IIIT Allahabad',
  'DTU', 'NSUT', 'IGDTUW', 'Jadavpur University', 'Anna University',
];

const SKILLS_LIST = [
  'Python', 'JavaScript', 'TypeScript', 'React', 'Node.js', 'Java', 'C++', 'Go', 'Rust',
  'Machine Learning', 'Deep Learning', 'Data Analysis', 'TensorFlow', 'PyTorch',
  'AWS', 'Docker', 'Kubernetes', 'MongoDB', 'PostgreSQL', 'MySQL',
  'Flutter', 'Android', 'iOS', 'Next.js', 'Django', 'Flask', 'GraphQL',
  'Redis', 'Elasticsearch', 'Kotlin', 'Swift', 'R', 'Vue.js', 'Angular',
  'HTML/CSS', 'Redux', 'FastAPI', 'Spring Boot', 'GCP', 'Azure', 'CI/CD',
  'Linux', 'Pandas', 'NumPy', 'NLP', 'LLMs', 'React Native', 'REST APIs',
  'Microservices', 'System Design', 'Product Management', 'UI/UX Design',
  'Figma', 'Adobe XD', 'Digital Marketing', 'SEO', 'Growth Hacking',
  'Financial Modeling', 'Excel/Sheets', 'Content Writing', 'Video Editing',
  'Blockchain', 'Game Development', 'AR/VR', 'Solidity',
];

const INTERVIEW_STEP_SUGGESTIONS = [
  'Phone screening', 'Technical assessment', 'Online test', 'Take-home project',
  'Technical interview', 'Culture fit interview', 'Final interview', 'Portfolio review',
];

const EMPTY_FORM = {
  title: '',
  company_name: '',
  role_category: '',
  position_type: 'Internship',
  work_mode: 'Remote',
  location: '',
  duration_months: '',
  start_date: '',
  stipend_min: '',
  stipend_max: '',
  stipend_currency: 'INR',
  salary_type: 'Monthly',
  application_deadline: '',
  num_openings: '',
  description: '',
  responsibilities: [''],
  skills: [],
  experience: '',
  preferred_colleges: [],
  screening_questions: [],
  visibility: 'public',
  interview_steps: [],
};

function TagSelector({ label, items, selected, onToggle, searchable = true, max = null }) {
  const [search, setSearch] = useState('');
  const filtered = searchable
    ? items.filter(i => i.toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div className="tag-selector">
      {searchable && (
        <input
          className="tag-selector-search"
          type="text"
          placeholder={`Search ${label.toLowerCase()}…`}
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label={`Search ${label}`}
        />
      )}
      <div className="tag-selector-chips">
        {filtered.slice(0, 40).map(item => (
          <button
            key={item}
            type="button"
            className={`tag-chip${selected.includes(item) ? ' selected' : ''}`}
            onClick={() => {
              if (!selected.includes(item) && max && selected.length >= max) return;
              onToggle(item);
            }}
            aria-pressed={selected.includes(item)}
          >
            {item}
            {selected.includes(item) && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ marginLeft: 4 }}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </button>
        ))}
      </div>
      {selected.length > 0 && (
        <div className="tag-selector-selected">
          {selected.map(s => (
            <span key={s} className="selected-tag">
              {s}
              <button type="button" onClick={() => onToggle(s)} aria-label={`Remove ${s}`}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function StepIndicator({ current, total }) {
  return (
    <div className="pj-steps" role="progressbar" aria-valuenow={current} aria-valuemin={1} aria-valuemax={total}>
      {Array.from({ length: total }, (_, i) => (
        <React.Fragment key={i}>
          <div className={`pj-step${i + 1 === current ? ' active' : i + 1 < current ? ' done' : ''}`}>
            <div className="pj-step-circle">
              {i + 1 < current
                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                : i + 1}
            </div>
            <span className="pj-step-label">
              {['Basic Info', 'Details', 'Preview'][i]}
            </span>
          </div>
          {i < total - 1 && <div className={`pj-step-line${i + 1 < current ? ' done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function JobPreview({ form, recruiter }) {
  const salaryPeriod = form.salary_type === 'Annual' ? 'year' : 'month';
  const stipendText = form.stipend_min && form.stipend_max
    ? `${form.stipend_currency} ${Number(form.stipend_min).toLocaleString('en-IN')} – ${Number(form.stipend_max).toLocaleString('en-IN')} / ${salaryPeriod}`
    : form.stipend_min
      ? `${form.stipend_currency} ${Number(form.stipend_min).toLocaleString('en-IN')}+ / ${salaryPeriod}`
      : 'Not specified';

  return (
    <div className="pj-preview-card">
      <div className="pj-preview-header">
        <div>
          <h2 className="pj-preview-title">{form.title || 'Job Title'}</h2>
          <div className="pj-preview-company">{form.company_name || recruiter?.company_name || 'Your Company'}</div>
        </div>
        <div className="pj-preview-badges">
          <span className="job-badge">{form.position_type}</span>
          <span className="job-badge job-badge-mode">{form.work_mode}</span>
          {form.visibility === 'private' && <span className="job-badge job-badge-private">Confidential</span>}
        </div>
      </div>

      <div className="pj-preview-meta">
        {form.role_category && <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> {form.role_category}</span>}
        {form.location && <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> {form.location}</span>}
        {form.duration_months && <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> {form.duration_months} month{form.duration_months > 1 ? 's' : ''}</span>}
        <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> {stipendText}</span>
        {form.num_openings && <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> {form.num_openings} opening{form.num_openings > 1 ? 's' : ''}</span>}
        {form.application_deadline && <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> Apply by {new Date(form.application_deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
      </div>

      {form.description && (
        <div className="pj-preview-section">
          <h4>About the role</h4>
          <p style={{ whiteSpace: 'pre-line', fontSize: 14 }}>{form.description}</p>
        </div>
      )}

      {form.responsibilities.filter(Boolean).length > 0 && (
        <div className="pj-preview-section">
          <h4>Responsibilities</h4>
          <ul className="pj-preview-list">
            {form.responsibilities.filter(Boolean).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}

      {form.skills.length > 0 && (
        <div className="pj-preview-section">
          <h4>Skills required</h4>
          <div className="pj-preview-tags">
            {form.skills.map(s => <span key={s} className="skill-tag">{s}</span>)}
          </div>
        </div>
      )}

      {form.preferred_colleges.length > 0 && (
        <div className="pj-preview-section">
          <h4>Preferred institutions</h4>
          <div className="pj-preview-tags">
            {form.preferred_colleges.map(c => <span key={c} className="skill-tag" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>{c}</span>)}
          </div>
        </div>
      )}

      {form.screening_questions.length > 0 && (
        <div className="pj-preview-section">
          <h4>Application questions</h4>
          <ol className="pj-preview-list" style={{ paddingLeft: 20 }}>
            {form.screening_questions.map((q, i) => <li key={i} style={{ marginBottom: 6 }}>{q.text}</li>)}
          </ol>
        </div>
      )}

      {form.interview_steps.length > 0 && (
        <div className="pj-preview-section">
          <h4>Interview process</h4>
          <div className="pj-interview-steps">
            {form.interview_steps.map((step, i) => (
              <div key={i} className="pj-interview-step">
                <div className="pj-interview-step-num">{i + 1}</div>
                <span>{step}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PostJob({ onLogout }) {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const token = localStorage.getItem('token');
  const recruiter = JSON.parse(localStorage.getItem('user') || '{}');

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...EMPTY_FORM, company_name: recruiter?.company_name || '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Set when publishing is blocked for want of a job credit. The draft is
  // already saved at that point, so this is an offer to continue, not an error.
  const [needsCredits, setNeedsCredits] = useState(null);
  const [packages, setPackages] = useState(null);
  const [checkout, setCheckout] = useState({ open: false, pkg: null });
  const [toast, setToast] = useState(null);
  const [jobId, setJobId] = useState(editId || null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (editId) loadJob(editId);
  }, [editId]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadJob = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/jobs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = res.data;
      setForm({
        title: j.title || '',
        company_name: j.company_name || recruiter?.company_name || '',
        role_category: j.role_category || '',
        position_type: j.position_type || 'Internship',
        work_mode: j.work_mode || 'Remote',
        location: j.location || '',
        duration_months: j.duration_months || '',
        start_date: j.start_date ? j.start_date.slice(0, 10) : '',
        stipend_min: j.stipend?.min || '',
        stipend_max: j.stipend?.max || '',
        stipend_currency: j.stipend?.currency || 'INR',
        salary_type: j.salary_type || 'Monthly',
        application_deadline: j.application_deadline ? j.application_deadline.slice(0, 10) : '',
        num_openings: j.num_openings || '',
        description: j.description || '',
        responsibilities: j.responsibilities?.length ? j.responsibilities : [''],
        skills: j.skills || [],
        experience: j.experience || '',
        preferred_colleges: j.preferred_colleges || [],
        screening_questions: j.screening_questions || [],
        visibility: j.visibility || 'public',
        interview_steps: j.interview_steps || [],
      });
    } catch {
      showToast('Could not load job', 'error');
    }
  };

  const set = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    setErrors(e => { const n = { ...e }; delete n[key]; return n; });
  };

  const validateStep1 = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Job title is required';
    if (!form.role_category) e.role_category = 'Select a role category';
    if (!form.position_type) e.position_type = 'Select a position type';
    if (!form.work_mode) e.work_mode = 'Select a work mode';
    if (form.work_mode !== 'Remote' && !form.location.trim()) e.location = 'Location is required for on-site/hybrid roles';
    if (form.stipend_min && form.stipend_max && Number(form.stipend_min) > Number(form.stipend_max)) {
      e.stipend_min = 'Minimum cannot exceed maximum';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.description.trim() || form.description.trim().length < 50) {
      e.description = 'Description must be at least 50 characters';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = (status) => ({
    title: form.title.trim(),
    company_name: form.company_name.trim() || undefined,
    role_category: form.role_category,
    position_type: form.position_type,
    work_mode: form.work_mode,
    location: form.location.trim() || undefined,
    duration_months: form.duration_months ? Number(form.duration_months) : undefined,
    start_date: form.start_date || undefined,
    stipend: (form.stipend_min || form.stipend_max) ? {
      min: form.stipend_min ? Number(form.stipend_min) : undefined,
      max: form.stipend_max ? Number(form.stipend_max) : undefined,
      currency: form.stipend_currency,
    } : undefined,
    salary_type: form.salary_type,
    application_deadline: form.application_deadline || undefined,
    num_openings: form.num_openings ? Number(form.num_openings) : undefined,
    description: form.description.trim(),
    responsibilities: form.responsibilities.filter(r => r.trim()),
    skills: form.skills,
    experience: form.experience.trim() || undefined,
    preferred_colleges: form.preferred_colleges,
    screening_questions: form.screening_questions.filter(q => q.text.trim()),
    visibility: form.visibility,
    interview_steps: form.interview_steps.filter(s => s.trim()),
    status,
  });

  const saveDraft = useCallback(async () => {
    setSaving(true);
    try {
      const payload = buildPayload('draft');
      let res;
      if (jobId) {
        res = await axios.put(`${API_URL}/api/jobs/${jobId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        res = await axios.post(`${API_URL}/api/jobs`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setJobId(res.data._id);
      }
      showToast('Draft saved');
    } catch (err) {
      showToast(err.response?.data?.error || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  }, [form, jobId, token]);

  // Publishing is the point at which a job costs a credit, so it is deliberately
  // two steps: save the content as a draft first, then ask the server to take it
  // live. If the recruiter has no job credits the second step returns 402 — and
  // because the first step already ran, their work is safely saved as a draft
  // rather than lost behind a paywall.
  //
  // The status change must go through PUT /:id/status: the ordinary edit
  // endpoint ignores `status` precisely so publishing cannot bypass the charge.
  useEffect(() => {
    fetchPackages().then(setPackages).catch(() => { /* the picker falls back to a skeleton */ });
  }, []);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = buildPayload('draft');

      let id = jobId;
      if (id) {
        await axios.put(`${API_URL}/api/jobs/${id}`, payload, { headers });
      } else {
        const created = await axios.post(`${API_URL}/api/jobs`, payload, { headers });
        id = created.data._id;
        setJobId(id);
      }

      const res = await axios.put(
        `${API_URL}/api/jobs/${id}/status`,
        { status: 'published' },
        { headers }
      );
      navigate('/recruiter/dashboard', { state: { jobPublished: res.data.title, jobId: res.data._id } });
    } catch (err) {
      if (err.response?.status === 402) {
        setNeedsCredits(err.response.data?.error || 'You need a job credit to publish this role.');
        return;
      }
      showToast(err.response?.data?.error || 'Publish failed', 'error');
    } finally {
      setPublishing(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    setStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const addResponsibility = () => set('responsibilities', [...form.responsibilities, '']);
  const updateResponsibility = (i, val) => {
    const arr = [...form.responsibilities];
    arr[i] = val;
    set('responsibilities', arr);
  };
  const removeResponsibility = (i) => set('responsibilities', form.responsibilities.filter((_, idx) => idx !== i));

  const addScreeningQ = () => set('screening_questions', [...form.screening_questions, { text: '' }]);
  const updateScreeningQ = (i, val) => {
    const arr = [...form.screening_questions];
    arr[i] = { ...arr[i], text: val };
    set('screening_questions', arr);
  };
  const removeScreeningQ = (i) => set('screening_questions', form.screening_questions.filter((_, idx) => idx !== i));

  const addInterviewStep = (val) => {
    if (!form.interview_steps.includes(val)) set('interview_steps', [...form.interview_steps, val]);
  };
  const removeInterviewStep = (i) => set('interview_steps', form.interview_steps.filter((_, idx) => idx !== i));

  const toggleSkill = (s) => set('skills', form.skills.includes(s) ? form.skills.filter(x => x !== s) : [...form.skills, s]);
  const toggleCollege = (c) => set('preferred_colleges', form.preferred_colleges.includes(c) ? form.preferred_colleges.filter(x => x !== c) : [...form.preferred_colleges, c]);

  const Err = ({ field }) => errors[field] ? <div className="field-error" role="alert">{errors[field]}</div> : null;

  return (
    <div className="pj-page">
      {/* Toast */}
      {toast && (
        <div className={`pj-toast pj-toast-${toast.type}`} role="alert">
          {toast.type === 'success'
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
          {toast.msg}
        </div>
      )}

      {/* Navbar */}
      <nav className="pj-nav">
        <button className="pj-nav-back" onClick={() => navigate('/recruiter/dashboard')} aria-label="Back to dashboard">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
          Dashboard
        </button>
        <div className="pj-nav-title">
          {editId ? 'Edit Job' : 'Post a Job'}
        </div>
        <button
          className="pj-nav-draft"
          onClick={saveDraft}
          disabled={saving}
          type="button"
        >
          {saving ? 'Saving…' : 'Save Draft'}
        </button>
      </nav>

      <div className="pj-container">
        <StepIndicator current={step} total={3} />

        {/* ── STEP 1: BASIC INFO ── */}
        {step === 1 && (
          <div className="pj-section">
            <div className="pj-section-header">
              <h2>Basic information</h2>
              <p>These details help candidates discover your role</p>
            </div>

            <div className="pj-card">
              <div className="pj-row-2">
                {/* Title */}
                <div className={`form-group${errors.title ? ' has-error' : ''}`}>
                  <label htmlFor="title">Job title <span className="req">*</span></label>
                  <input
                    id="title"
                    type="text"
                    value={form.title}
                    onChange={e => set('title', e.target.value)}
                    placeholder="e.g. Product Engineer Intern"
                    maxLength={120}
                    required
                    aria-required="true"
                    aria-describedby={errors.title ? 'title-err' : undefined}
                    aria-invalid={!!errors.title}
                  />
                  <Err field="title" />
                </div>

                {/* Company name */}
                <div className="form-group">
                  <label htmlFor="company_name">Company name <span className="req">*</span></label>
                  <input
                    id="company_name"
                    type="text"
                    value={form.company_name}
                    onChange={e => set('company_name', e.target.value)}
                    placeholder="e.g. Acme Corp"
                    maxLength={120}
                  />
                </div>
              </div>

              <div className="pj-row-2">
                {/* Role Category */}
                <div className={`form-group${errors.role_category ? ' has-error' : ''}`}>
                  <label htmlFor="role_category">Role category <span className="req">*</span></label>
                  <select
                    id="role_category"
                    value={form.role_category}
                    onChange={e => set('role_category', e.target.value)}
                    required
                    aria-required="true"
                    aria-invalid={!!errors.role_category}
                  >
                    <option value="">Select category</option>
                    {ROLE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <Err field="role_category" />
                </div>

                {/* Position Type */}
                <div className={`form-group${errors.position_type ? ' has-error' : ''}`}>
                  <label htmlFor="position_type">Position type <span className="req">*</span></label>
                  <select
                    id="position_type"
                    value={form.position_type}
                    onChange={e => set('position_type', e.target.value)}
                    required
                    aria-required="true"
                  >
                    {POSITION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <Err field="position_type" />
                </div>
              </div>

              {/* Work Mode — Remote only */}
              <div className="form-group">
                <label>Work mode <span className="req">*</span></label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--primary-light)', borderRadius: 8, border: '1px solid var(--primary)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                  <span style={{ fontWeight: 600, color: 'var(--primary)' }}>Remote</span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 6 }}>— Only remote opportunities can be created at this time</span>
                </div>
              </div>

              <div className="pj-row-2">
                {/* Duration */}
                <div className="form-group">
                  <label htmlFor="duration_months">Duration (months)</label>
                  <select
                    id="duration_months"
                    value={form.duration_months}
                    onChange={e => set('duration_months', e.target.value)}
                  >
                    <option value="">No fixed term</option>
                    {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d} month{d > 1 ? 's' : ''}</option>)}
                  </select>
                </div>

                {/* Start date */}
                <div className="form-group">
                  <label htmlFor="start_date">Start date</label>
                  <input
                    id="start_date"
                    type="date"
                    value={form.start_date}
                    onChange={e => set('start_date', e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </div>
              </div>

              {/* Stipend */}
              <fieldset className="form-group">
                <legend>
                  Expected salary
                  <span style={{ marginLeft: 10 }}>
                    {SALARY_TYPES.map(t => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => set('salary_type', t)}
                        style={{
                          marginLeft: 4, padding: '2px 10px', fontSize: 12, fontWeight: 600,
                          borderRadius: 12, border: '1px solid var(--border)', cursor: 'pointer',
                          background: form.salary_type === t ? 'var(--primary)' : 'var(--surface)',
                          color: form.salary_type === t ? '#fff' : 'var(--text-muted)',
                          transition: 'background 0.15s, color 0.15s',
                        }}
                      >{t}</button>
                    ))}
                  </span>
                </legend>
                <div className="pj-stipend-row">
                  <div className={`pj-stipend-input${errors.stipend_min ? ' has-error' : ''}`}>
                    <input
                      type="number"
                      value={form.stipend_min}
                      onChange={e => set('stipend_min', e.target.value)}
                      placeholder="Min (e.g. 10000)"
                      min={0}
                      aria-label="Minimum stipend"
                      aria-invalid={!!errors.stipend_min}
                    />
                  </div>
                  <span className="pj-stipend-dash">—</span>
                  <div className="pj-stipend-input">
                    <input
                      type="number"
                      value={form.stipend_max}
                      onChange={e => set('stipend_max', e.target.value)}
                      placeholder="Max (e.g. 20000)"
                      min={0}
                      aria-label="Maximum stipend"
                    />
                  </div>
                  <select
                    value={form.stipend_currency}
                    onChange={e => set('stipend_currency', e.target.value)}
                    aria-label="Currency"
                    className="pj-currency-select"
                  >
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                {errors.stipend_min && <div className="field-error">{errors.stipend_min}</div>}
                <div className="form-hint">
                  {form.salary_type === 'Annual' ? 'Annual package (e.g. 600000 for ₹6 LPA)' : 'Monthly stipend (e.g. 15000 for ₹15k/month)'}
                  {' · '}Listing a range improves application quality
                </div>
              </fieldset>

              <div className="pj-row-2">
                {/* Deadline */}
                <div className="form-group">
                  <label htmlFor="application_deadline">Application deadline</label>
                  <input
                    id="application_deadline"
                    type="date"
                    value={form.application_deadline}
                    onChange={e => set('application_deadline', e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </div>

                {/* Openings */}
                <div className="form-group">
                  <label htmlFor="num_openings">No. of openings</label>
                  <input
                    id="num_openings"
                    type="number"
                    value={form.num_openings}
                    onChange={e => set('num_openings', e.target.value)}
                    placeholder="1"
                    min={1}
                    max={100}
                  />
                </div>
              </div>

              {/* Visibility */}
              <div className="form-group">
                <label>Listing visibility</label>
                <div className="pj-visibility-toggle">
                  <button
                    type="button"
                    className={`pj-vis-btn${form.visibility === 'public' ? ' active' : ''}`}
                    onClick={() => set('visibility', 'public')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                    Public
                  </button>
                  <button
                    type="button"
                    className={`pj-vis-btn${form.visibility === 'private' ? ' active' : ''}`}
                    onClick={() => set('visibility', 'private')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    Confidential
                  </button>
                </div>
                <div className="form-hint">Confidential hides your company name from candidates</div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2: DETAILS ── */}
        {step === 2 && (
          <div className="pj-section">
            <div className="pj-section-header">
              <h2>Role details</h2>
              <p>Help candidates understand what they'll do and what you're looking for</p>
            </div>

            {/* Description */}
            <div className="pj-card">
              <div className={`form-group${errors.description ? ' has-error' : ''}`}>
                <label htmlFor="description">
                  Job description <span className="req">*</span>
                  <span className="form-hint" style={{ marginLeft: 8 }}>{form.description.length} chars</span>
                </label>
                <textarea
                  id="description"
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  placeholder="Describe the role, team, and what the candidate will be working on. Use bullet points for clarity."
                  rows={6}
                  required
                  aria-required="true"
                  aria-invalid={!!errors.description}
                  style={{ resize: 'vertical' }}
                />
                <Err field="description" />
              </div>

              {/* Responsibilities */}
              <div className="form-group">
                <label>Key responsibilities</label>
                {form.responsibilities.map((r, i) => (
                  <div key={i} className="pj-list-row">
                    <input
                      type="text"
                      value={r}
                      onChange={e => updateResponsibility(i, e.target.value)}
                      placeholder={`Responsibility ${i + 1}`}
                      aria-label={`Responsibility ${i + 1}`}
                    />
                    {form.responsibilities.length > 1 && (
                      <button type="button" className="pj-list-remove" onClick={() => removeResponsibility(i)} aria-label="Remove">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="pj-add-btn" onClick={addResponsibility}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add responsibility
                </button>
              </div>
            </div>

            {/* Skills */}
            <div className="pj-card">
              <div className="pj-card-heading">
                <h3>Required skills</h3>
                <span className="pj-card-hint">Select technologies and skills</span>
              </div>
              <TagSelector
                label="Skills"
                items={SKILLS_LIST}
                selected={form.skills}
                onToggle={toggleSkill}
              />
            </div>

            {/* Advanced toggle */}
            <button
              type="button"
              className="pj-advanced-toggle"
              onClick={() => setShowAdvanced(v => !v)}
              aria-expanded={showAdvanced}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
              </svg>
              {showAdvanced ? 'Hide' : 'Show'} advanced options
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 'auto', transform: showAdvanced ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {showAdvanced && (
              <>
                {/* Experience */}
                <div className="pj-card">
                  <div className="pj-card-heading">
                    <h3>Experience & eligibility</h3>
                  </div>
                  <div className="form-group">
                    <label htmlFor="experience">Experience level / year</label>
                    <input
                      id="experience"
                      type="text"
                      value={form.experience}
                      onChange={e => set('experience', e.target.value)}
                      placeholder="e.g. 3rd or 4th year CS student, Fresher"
                    />
                  </div>
                </div>

                {/* Preferred colleges */}
                <div className="pj-card">
                  <div className="pj-card-heading">
                    <h3>Preferred institutions</h3>
                    <span className="pj-card-hint">Optional — leave blank to accept from any college</span>
                  </div>
                  <TagSelector
                    label="Colleges"
                    items={COLLEGES}
                    selected={form.preferred_colleges}
                    onToggle={toggleCollege}
                  />
                </div>

                {/* Screening questions */}
                <div className="pj-card">
                  <div className="pj-card-heading">
                    <h3>Screening questions</h3>
                    <span className="pj-card-hint">Candidates answer these when applying</span>
                  </div>
                  {form.screening_questions.map((q, i) => (
                    <div key={i} className="pj-list-row" style={{ marginBottom: 10 }}>
                      <input
                        type="text"
                        value={q.text}
                        onChange={e => updateScreeningQ(i, e.target.value)}
                        placeholder={`Question ${i + 1} — e.g. Why are you interested in this role?`}
                        aria-label={`Screening question ${i + 1}`}
                      />
                      <button type="button" className="pj-list-remove" onClick={() => removeScreeningQ(i)} aria-label="Remove question">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  ))}
                  {form.screening_questions.length < 5 && (
                    <button type="button" className="pj-add-btn" onClick={addScreeningQ}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Add question
                    </button>
                  )}
                </div>

                {/* Interview steps */}
                <div className="pj-card">
                  <div className="pj-card-heading">
                    <h3>Interview process</h3>
                    <span className="pj-card-hint">Give candidates a clear picture of what to expect</span>
                  </div>
                  <div className="pj-interview-suggestions">
                    {INTERVIEW_STEP_SUGGESTIONS.map(s => (
                      <button
                        key={s}
                        type="button"
                        className={`tag-chip${form.interview_steps.includes(s) ? ' selected' : ''}`}
                        onClick={() => form.interview_steps.includes(s) ? removeInterviewStep(form.interview_steps.indexOf(s)) : addInterviewStep(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  {form.interview_steps.length > 0 && (
                    <div className="pj-interview-steps" style={{ marginTop: 14 }}>
                      {form.interview_steps.map((step, i) => (
                        <div key={i} className="pj-interview-step">
                          <div className="pj-interview-step-num">{i + 1}</div>
                          <span style={{ flex: 1 }}>{step}</span>
                          <button type="button" className="pj-list-remove" onClick={() => removeInterviewStep(i)} aria-label="Remove step">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── STEP 3: PREVIEW ── */}
        {step === 3 && (
          <div className="pj-section">
            <div className="pj-section-header">
              <h2>Preview &amp; publish</h2>
              <p>This is how your job will appear to candidates</p>
            </div>
            <JobPreview form={form} recruiter={recruiter} />
            <div className="pj-publish-box">
              <div className="pj-publish-info">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <span>Once published, verified students matching your criteria will see this listing immediately.</span>
              </div>
              <div className="pj-publish-actions">
                <button type="button" className="btn btn-secondary" onClick={saveDraft} disabled={saving}>
                  {saving ? 'Saving…' : 'Save as Draft'}
                </button>
                <button type="button" className="btn btn-primary pj-publish-btn" onClick={handlePublish} disabled={publishing}>
                  {publishing
                    ? <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Publishing…</>
                    : <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4 20-7z"/></svg>
                        Publish Job
                      </>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bottom nav */}
        <div className="pj-bottom-nav">
          {step > 1 && (
            <button type="button" className="btn btn-secondary" onClick={prevStep}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
              Back
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
            <button type="button" className="pj-draft-link" onClick={saveDraft} disabled={saving}>
              {saving ? 'Saving…' : 'Save Draft'}
            </button>
            {step < 3 && (
              <button type="button" className="btn btn-primary" onClick={nextStep}>
                {step === 1 ? 'Next: Role Details' : 'Preview Job'}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Job credits required ──
          Reached only after the draft has been saved, so the recruiter's work
          is safe and this is a decision to continue rather than a lost form. */}
      <Modal
        open={!!needsCredits}
        onClose={() => setNeedsCredits(null)}
        maxWidth={860}
        labelledBy="pj-credits-title"
      >
        <div style={{ padding: '28px 26px 24px' }}>
          <h2 id="pj-credits-title" style={{ fontSize: 20, marginBottom: 6 }}>
            You need a job credit to publish
          </h2>
          <p style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 20 }}>
            {needsCredits} Your job has been saved as a draft — buy a package and it
            will publish straight away.
          </p>
          {packages ? (
            <PricingPlans
              packages={packages}
              onSelect={(pkg) => { setNeedsCredits(null); setCheckout({ open: true, pkg }); }}
              disabled={packages.payments_enabled === false}
              products={['job']}
              compact
            />
          ) : (
            <div className="bl-plans">
              {[0, 1, 2].map(i => <div key={i} className="ui-skel" style={{ height: 300, borderRadius: 16 }} />)}
            </div>
          )}
        </div>
      </Modal>

      <CheckoutModal
        open={checkout.open}
        pkg={checkout.pkg}
        onClose={() => setCheckout({ open: false, pkg: null })}
        onSuccess={() => {
          // Credits are live the moment verification returns, so finish the job
          // the recruiter was trying to publish rather than making them find it
          // again in their drafts.
          setCheckout({ open: false, pkg: null });
          handlePublish();
        }}
      />
    </div>
  );
}
